import React, { useEffect, useState } from 'react';
import MapComponent from './Map';
import type { MarkerData } from '../types/markers';
import './MapView.css';

interface MapViewProps {
    markers: MarkerData[];
    onImageClick: (marker: MarkerData) => void;
    onMarkerDragEnd: (marker: MarkerData, newPosition: { lat: number; lng: number }) => void;
    onRequestDeleteMarker: (marker: MarkerData) => void;
    onRequestDeleteDuplicates: (marker: MarkerData) => void;
    isDuplicateMode: boolean;
    onRequestMoveMarker: (marker: MarkerData) => Promise<boolean> | boolean;
    onRequestMoreLikeThis: (marker: MarkerData) => void;
    showMoreLikeThis: boolean;
    onFullscreenChange?: (isFullscreen: boolean) => void;
    pendingMoveTarget: MarkerData | null;
    onConsumePendingMove: () => void;
    onCrosshairModeChange?: (active: boolean) => void;
    sourceMarkerId?: string | null;
    autoFitToken?: number;
    fitMarkers?: MarkerData[];
    isAutoZoomEnabled?: boolean;
}

const MapView: React.FC<MapViewProps> = ({
    markers,
    onImageClick,
    onMarkerDragEnd,
    onRequestDeleteMarker,
    onRequestDeleteDuplicates,
    isDuplicateMode,
    onRequestMoveMarker,
    onRequestMoreLikeThis,
    showMoreLikeThis,
    onFullscreenChange,
    pendingMoveTarget,
    onConsumePendingMove,
    onCrosshairModeChange,
    sourceMarkerId,
    autoFitToken,
    fitMarkers,
    isAutoZoomEnabled,
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            // Entering fullscreen: push state
            window.history.pushState({ fullscreen: 'map' }, '');
            setIsFullscreen(true);
        } else {
            // Exiting fullscreen: go back
            window.history.back();
            // State update will happen in popstate listener or we can do it here optimistically,
            // but relying on popstate is safer for consistency if we want single source of truth.
            // However, history.back() is async. Let's handle state change in popstate or
            // if we want immediate feedback, we can set it here too, but we must be careful not to double-fire.
            // Actually, simpler approach:
            // If we trigger back(), the popstate event will fire.
        }
    };

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            // If we are navigating back, check if we should exit fullscreen
            // In this simple case, if we pushed state for fullscreen, popping it means we exit.
            // We can check if the current state has our flag, but actually when we go back,
            // we are reverting to the state BEFORE fullscreen.
            // So if isFullscreen is true, and we get a popstate, it likely means we are going back.

            // A more robust way:
            // When we enter fullscreen, we push state.
            // When we hit back, we return to a state WITHOUT { fullscreen: 'map' }.
            // So we can check history.state.

            const state = event.state as { fullscreen?: string } | null;
            if (state?.fullscreen === 'map') {
                setIsFullscreen(true);
            } else {
                setIsFullscreen(false);
            }
        };

        window.addEventListener('popstate', handlePopState);

        // Handle case where user might have reloaded page while in fullscreen state (unlikely but good to be safe)
        // or if we want to sync initial state.
        const currentState = window.history.state as { fullscreen?: string } | null;
        if (currentState?.fullscreen === 'map') {
            setIsFullscreen(true);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        const lockClass = 'map-view--body-locked';
        if (isFullscreen) {
            document.body.classList.add(lockClass);
        } else {
            document.body.classList.remove(lockClass);
        }
        return () => document.body.classList.remove(lockClass);
    }, [isFullscreen]);

    useEffect(() => {
        onFullscreenChange?.(isFullscreen);
        // If component unmounts while fullscreen, we should probably clean up history?
        // It's tricky. If user navigates away, history changes anyway.
        // If component unmounts but we stay on same page (e.g. tab switch), we might want to exit fullscreen.
        return () => {
            onFullscreenChange?.(false);
        };
    }, [isFullscreen, onFullscreenChange]);

    useEffect(() => {
        if (!isFullscreen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // If we use Escape, we should also pop history to keep it in sync
                window.history.back();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    const mapContainerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isFullscreen || !mapContainerRef.current) return;

        const handleVisualViewport = () => {
            if (!window.visualViewport || !mapContainerRef.current) return;

            const vv = window.visualViewport;
            const style = mapContainerRef.current.style;

            // Dynamically position and size the map to match the visible viewport
            // This effectively "resets" the zoom for the map container relative to the user's view
            style.position = 'fixed';
            style.left = `${vv.offsetLeft}px`;
            style.top = `${vv.offsetTop}px`;
            style.width = `${vv.width}px`;
            style.height = `${vv.height}px`;
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleVisualViewport);
            window.visualViewport.addEventListener('scroll', handleVisualViewport);
            // Initial call to set correct state immediately
            handleVisualViewport();
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleVisualViewport);
                window.visualViewport.removeEventListener('scroll', handleVisualViewport);
            }
            // Reset styles on cleanup
            if (mapContainerRef.current) {
                const style = mapContainerRef.current.style;
                style.position = '';
                style.left = '';
                style.top = '';
                style.width = '';
                style.height = '';
            }
        };
    }, [isFullscreen]);

    const mapContent = (
        <div
            ref={mapContainerRef}
            className={`map-view ${isFullscreen ? 'map-view--fullscreen' : ''}`}
        >
            <div className="map-view__controls">
                <button
                    type="button"
                    className={`map-view__fullscreen-btn ${isFullscreen ? 'map-view__fullscreen-btn--icon' : ''}`}
                    aria-pressed={isFullscreen}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen map"}
                >
                    {isFullscreen ? (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                            <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                            <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                            <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                        </svg>
                    ) : (
                        'Fullscreen map'
                    )}
                </button>
            </div>
            <MapComponent
                markers={markers}
                onImageClick={onImageClick}
                onMarkerDragEnd={onMarkerDragEnd}
                onRequestDeleteMarker={onRequestDeleteMarker}
                onRequestDeleteDuplicates={onRequestDeleteDuplicates}
                isDuplicateMode={isDuplicateMode}
                onRequestMoveMarker={onRequestMoveMarker}
                onRequestMoreLikeThis={onRequestMoreLikeThis}
                showMoreLikeThis={showMoreLikeThis}
                pendingMoveTarget={pendingMoveTarget}
                onConsumePendingMove={onConsumePendingMove}
                onCrosshairModeChange={onCrosshairModeChange}
                sourceMarkerId={sourceMarkerId}
                autoFitToken={autoFitToken}
                fitMarkers={fitMarkers}
                isAutoZoomEnabled={isAutoZoomEnabled}
            />
        </div>
    );

    return mapContent;
};

export default MapView;
